import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowRight, X, Check, ExternalLink, Facebook, Instagram } from 'lucide-react';
import { Dialog, DialogContent } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// TikTok Icon Component
const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Brand Card Component
const BrandCard = ({ brand, onClick }) => (
  <div
    className="card-dark rounded-xl overflow-hidden cursor-pointer group"
    onClick={() => onClick(brand)}
    data-testid={`brand-card-${brand.id}`}
  >
    <div className="relative h-48 bg-[#f5f5f5] flex items-center justify-center overflow-hidden">
      {brand.logo ? (
        <img src={brand.logo} alt={brand.name} className="h-full w-auto max-w-full object-contain" />
      ) : (
        <span className="text-6xl font-bold text-[#0A3D62]/30">{brand.name.charAt(0)}</span>
      )}
      <span className="absolute top-3 left-3 badge-category text-[10px]">{brand.category}</span>
      {brand.is_featured && (
        <span className="absolute top-3 right-3 bg-[#0A3D62] text-white px-3 py-1 rounded text-[10px] font-bold">FEATURED</span>
      )}
    </div>
    <div className="p-6">
      <h3 className="font-heading text-xl font-bold text-white mb-2">{brand.name}</h3>
      <p className="text-white/60 text-sm leading-relaxed line-clamp-2 mb-4">{brand.short_description}</p>
      <div className="flex items-center justify-between">
        {brand.total_investment && (
          <div>
            <p className="text-white/40 text-xs uppercase">Investment</p>
            <p className="text-accent font-semibold text-sm">From {brand.total_investment.split('-')[0].trim()}</p>
          </div>
        )}
        <span className="text-accent flex items-center gap-1 text-sm group-hover:gap-2 transition-all">
          View Details <ArrowRight className="w-4 h-4" />
        </span>
      </div>
    </div>
  </div>
);

// Brand Detail Modal
const BrandDetailModal = ({ brand, isOpen, onClose }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState('');
  
  if (!brand) return null;

  const openLightbox = (img) => {
    setLightboxImage(img);
    setLightboxOpen(true);
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent hideCloseButton className="max-w-4xl max-h-[90vh] overflow-y-auto bg-[#0A3D62] border-2 border-[#1a5276] p-0" data-testid="brand-detail-modal">
          <div className="relative">
            {/* Header with logo */}
            <div className="relative h-48 sm:h-72 bg-[#f5f5f5] flex items-center justify-center overflow-hidden">
              {brand.logo ? (
                <img src={brand.logo} alt={brand.name} className="h-full w-auto max-w-full object-contain" />
              ) : (
                <span className="text-6xl sm:text-8xl font-bold text-[#0A3D62]/30">{brand.name.charAt(0)}</span>
              )}
              {brand.is_featured && (
                <span className="absolute top-4 left-4 bg-[#0A3D62] text-white px-4 py-1 rounded text-xs font-bold">FEATURED</span>
              )}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#0A3D62] flex items-center justify-center text-white hover:bg-[#072942] transition-colors"
                data-testid="close-modal-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-4 sm:p-8">
              <span className="badge-category mb-4 inline-block">{brand.category}</span>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-4">{brand.name}</h2>
              
              {/* Gallery */}
              {brand.gallery && brand.gallery.length > 0 && (
                <div className="mb-8">
                  <h4 className="text-accent font-semibold mb-4">Gallery</h4>
                  <div className="grid grid-cols-3 sm:flex sm:flex-row gap-3 sm:overflow-x-auto pb-2">
                    {brand.gallery.map((img, index) => (
                      <img
                        key={index}
                        src={img}
                        alt={`${brand.name} gallery ${index + 1}`}
                        className="w-full sm:w-28 h-24 sm:h-28 rounded-lg object-cover flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity border-2 border-transparent hover:border-accent"
                        onClick={() => openLightbox(img)}
                      />
                    ))}
                  </div>
                </div>
              )}
            
              {/* Investment Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4 mb-8">
              {brand.franchise_fee && (
                <div className="bg-surface-highlight rounded-lg p-4">
                  <p className="text-white/40 text-xs uppercase mb-1">Franchise Fee</p>
                  <p className="text-accent font-semibold">{brand.franchise_fee}</p>
                </div>
              )}
              {brand.royalty_fee && (
                <div className="bg-surface-highlight rounded-lg p-4">
                  <p className="text-white/40 text-xs uppercase mb-1">Royalty Fee</p>
                  <p className="text-accent font-semibold">{brand.royalty_fee}</p>
                </div>
              )}
              {brand.total_investment && (
                <div className="bg-surface-highlight rounded-lg p-4">
                  <p className="text-white/40 text-xs uppercase mb-1">Initial Investment</p>
                  <p className="text-accent font-semibold">{brand.total_investment}</p>
                </div>
              )}
              {brand.space_required && (
                <div className="bg-surface-highlight rounded-lg p-4">
                  <p className="text-white/40 text-xs uppercase mb-1">Space Required</p>
                  <p className="text-accent font-semibold">{brand.space_required}</p>
                </div>
              )}
              {brand.established && (
                <div className="bg-surface-highlight rounded-lg p-4">
                  <p className="text-white/40 text-xs uppercase mb-1">Established</p>
                  <p className="text-accent font-semibold">{brand.established}</p>
                </div>
              )}
            </div>
            
            {/* About */}
            {brand.about && (
              <div className="mb-8">
                <h4 className="font-heading text-xl font-semibold text-white mb-3">About {brand.name}</h4>
                <p className="text-white/70 leading-relaxed">{brand.about}</p>
              </div>
            )}
            
            {/* Highlights & Support */}
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              {brand.key_highlights && brand.key_highlights.length > 0 && (
                <div>
                  <h4 className="font-heading text-lg font-semibold text-white mb-4">Key Highlights</h4>
                  <ul className="space-y-2">
                    {brand.key_highlights.map((item, index) => (
                      <li key={index} className="flex items-start gap-2 text-white/70">
                        <Check className="w-4 h-4 text-accent mt-1 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {brand.franchise_support && brand.franchise_support.length > 0 && (
                <div>
                  <h4 className="font-heading text-lg font-semibold text-white mb-4">Franchise Support</h4>
                  <ul className="space-y-2">
                    {brand.franchise_support.map((item, index) => (
                      <li key={index} className="flex items-start gap-2 text-white/70">
                        <Check className="w-4 h-4 text-accent mt-1 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            
            {/* Ideal For */}
            {brand.ideal_for && brand.ideal_for.length > 0 && (
              <div className="mb-8">
                <h4 className="font-heading text-lg font-semibold text-white mb-4">Ideal For</h4>
                <div className="flex flex-wrap gap-2">
                  {brand.ideal_for.map((item, index) => (
                    <span key={index} className="px-4 py-2 rounded-full border-2 border-border text-white/70 text-sm">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Company Info */}
            <div className="bg-surface-highlight rounded-xl p-6 mb-8">
              <h4 className="font-heading text-lg font-semibold text-white mb-4">Company Information</h4>
              <div className="grid md:grid-cols-2 gap-6">
                {brand.headquarters && (
                  <div>
                    <p className="text-white/40 text-xs uppercase mb-1">Headquarters</p>
                    <p className="text-white">{brand.headquarters}</p>
                  </div>
                )}
                {brand.global_outlets && (
                  <div>
                    <p className="text-white/40 text-xs uppercase mb-1">Total Outlets</p>
                    <p className="text-white">{brand.global_outlets}</p>
                  </div>
                )}
                {brand.website && (
                  <div>
                    <p className="text-white/40 text-xs uppercase mb-1">Website</p>
                    <a href={brand.website.startsWith('http') ? brand.website : `https://${brand.website}`} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">{brand.website}</a>
                  </div>
                )}
                {brand.show_contact_info !== false && brand.email && (
                  <div>
                    <p className="text-white/40 text-xs uppercase mb-1">Email</p>
                    <a href={`mailto:${brand.email}`} className="text-accent hover:underline">{brand.email}</a>
                  </div>
                )}
                {brand.show_contact_info !== false && brand.contact_person && (
                  <div>
                    <p className="text-white/40 text-xs uppercase mb-1">Contact Person</p>
                    <p className="text-white">{brand.contact_person}</p>
                  </div>
                )}
                {brand.show_contact_info !== false && brand.contact_number && (
                  <div>
                    <p className="text-white/40 text-xs uppercase mb-1">Contact Number</p>
                    <a href={`tel:${brand.contact_number}`} className="text-accent hover:underline">{brand.contact_number}</a>
                  </div>
                )}
              </div>
              
              {/* Social Media */}
              {(brand.facebook_url || brand.instagram_url || brand.tiktok_url) && (
                <div className="mt-6 pt-6 border-t border-border">
                  <p className="text-white/40 text-xs uppercase mb-3">Social Media</p>
                  <div className="flex gap-3">
                    {brand.facebook_url && (
                      <a href={brand.facebook_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white hover:opacity-80 transition-opacity">
                        <Facebook className="w-5 h-5" />
                      </a>
                    )}
                    {brand.instagram_url && (
                      <a href={brand.instagram_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-pink-500 flex items-center justify-center text-white hover:opacity-80 transition-opacity">
                        <Instagram className="w-5 h-5" />
                      </a>
                    )}
                    {brand.tiktok_url && (
                      <a href={brand.tiktok_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-black flex items-center justify-center text-white hover:opacity-80 transition-opacity">
                        <TikTokIcon className="w-5 h-5" />
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* CTA */}
            <Link
              to="/#register"
              onClick={onClose}
              className="btn-gold-filled w-full rounded-lg flex items-center justify-center gap-2"
              data-testid="brand-express-interest-btn"
            >
              Express Interest <ExternalLink className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    
    {/* Lightbox */}
    <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
      <DialogContent hideCloseButton className="max-w-5xl bg-black/95 border-none p-2" data-testid="lightbox-modal">
        <button
          onClick={() => setLightboxOpen(false)}
          className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors z-50"
        >
          <X className="w-6 h-6" />
        </button>
        <img
          src={lightboxImage}
          alt="Gallery full view"
          className="w-full h-auto max-h-[85vh] object-contain rounded-lg"
        />
      </DialogContent>
    </Dialog>
  </>
  );
};

// Floating Dots Background Component
const FloatingDots = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let dots = [];

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const createDots = () => {
      dots = [];
      const numDots = 60;
      for (let i = 0; i < numDots; i++) {
        dots.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          radius: Math.random() * 2.5 + 1,
          vx: (Math.random() - 0.5) * 0.4,
          vy: (Math.random() - 0.5) * 0.4,
          opacity: Math.random() * 0.4 + 0.15,
          fadeSpeed: (Math.random() - 0.5) * 0.008
        });
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      dots.forEach(dot => {
        dot.x += dot.vx;
        dot.y += dot.vy;
        dot.opacity += dot.fadeSpeed;
        
        if (dot.opacity <= 0.1 || dot.opacity >= 0.6) {
          dot.fadeSpeed = -dot.fadeSpeed;
        }
        
        if (dot.x < 0) dot.x = canvas.width;
        if (dot.x > canvas.width) dot.x = 0;
        if (dot.y < 0) dot.y = canvas.height;
        if (dot.y > canvas.height) dot.y = 0;
        
        ctx.beginPath();
        ctx.arc(dot.x, dot.y, dot.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(212, 168, 83, ${dot.opacity})`;
        ctx.fill();
      });
      
      animationFrameId = requestAnimationFrame(animate);
    };

    resizeCanvas();
    createDots();
    animate();

    const handleResize = () => {
      resizeCanvas();
      createDots();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 15 }}
    />
  );
};

// Main Brands Page Component
const BrandsPage = () => {
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await axios.get(`${API}/brands`);
        setBrands(response.data);
      } catch (error) {
        console.error('Error fetching brands:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBrands();
  }, []);

  const handleBrandClick = (brand) => {
    setSelectedBrand(brand);
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#0A3D62] relative" data-testid="brands-page">
      <FloatingDots />
      {/* Header */}
      <div className="relative z-10 py-8 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-8" data-testid="back-to-home-link">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="text-center mb-16">
          <p className="text-accent text-sm tracking-[0.3em] mb-4">PARTICIPATING BRANDS</p>
          <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
            <span className="text-accent">19</span>
            <span className="text-white"> THAI FRANCHISE </span>
            <span className="text-accent">BRANDS</span>
          </h1>
          <p className="text-white/60 max-w-2xl mx-auto">
            Discover Thailand's finest franchise opportunities, carefully curated for Singapore market success. Click on any brand to view detailed information.
          </p>
        </div>

        {loading ? (
          <div className="text-center text-white/60 py-16">Loading brands...</div>
        ) : brands.length === 0 ? (
          <div className="text-center text-white/60 py-16">No brands available yet. Check back soon!</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {brands.map((brand) => (
              <BrandCard key={brand.id} brand={brand} onClick={handleBrandClick} />
            ))}
          </div>
        )}
      </div>

      <BrandDetailModal
        brand={selectedBrand}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default BrandsPage;
